import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import useUpscCurrentAffairsCaArticle from '@api/hooks/upscCurrentAffairs/useUpscCurrentAffairsCaArticle';
import {
  UpscCurrentAffairsErrorState,
  UpscCurrentAffairsLoadingState
} from './UpscCurrentAffairsAsyncState';
import UpscCurrentAffairsDetailedPageHeader from './UpscCurrentAffairsDetailedPageHeader';
import UpscCurrentAffairsDetailedPageContent from './UpscCurrentAffairsDetailedPageContent';

const MINT_LEAF_SVG = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 280 280" fill="none">
    <g fill="#8fc4b6">
      <ellipse cx="86" cy="168" rx="26" ry="58" transform="rotate(-38 86 168)" opacity="0.28"/>
      <ellipse cx="128" cy="186" rx="22" ry="52" transform="rotate(18 128 186)" opacity="0.22"/>
      <ellipse cx="58" cy="206" rx="18" ry="42" transform="rotate(-58 58 206)" opacity="0.2"/>
      <ellipse cx="156" cy="214" rx="16" ry="36" transform="rotate(42 156 214)" opacity="0.16"/>
    </g>
    <path d="M48 248C78 188 98 128 78 58" stroke="#8fc4b6" stroke-width="1.6" opacity="0.28" fill="none"/>
  </svg>`
)}")`;

export const UpscDetailedPageCanvas = styled.div`
  position: relative;
  overflow: hidden;
  background: #f2faf7;

  &::before,
  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    width: min(280px, 48vw);
    height: min(280px, 48vw);
    pointer-events: none;
    background: ${MINT_LEAF_SVG} no-repeat center / contain;
  }

  &::before {
    left: -24px;
    bottom: -36px;
  }

  &::after {
    right: -24px;
    bottom: -36px;
    transform: scaleX(-1);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const PageWrap = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1136px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

const ArticleCard = styled.article`
  padding: 24px;
  border: 1px solid var(--color-divider);
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(60, 72, 82, 0.08);
`;

const NotFound = styled.div`
  padding: 32px 24px;
  border: 1px solid var(--color-divider);
  border-radius: 10px;
  background: #ffffff;
  text-align: center;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
`;

const UpscCurrentAffairsDetailedPageBody = () => {
  const router = useRouter();
  const { id } = router.query;
  const articleId = typeof id === 'string' ? id : undefined;
  const { article, isLoading, isError, retry } = useUpscCurrentAffairsCaArticle(
    router.isReady ? articleId : undefined
  );

  if (!router.isReady || isLoading) {
    return <UpscCurrentAffairsLoadingState variant="detail" />;
  }

  if (isError) {
    return (
      <UpscCurrentAffairsErrorState
        title="Unable to load article"
        subtitle="We could not fetch this current affairs article. Please try again."
        onRetry={retry}
      />
    );
  }

  if (!article) {
    return (
      <PageWrap>
        <NotFound>Current affairs article not found.</NotFound>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <ArticleCard aria-label={article.mainHeading}>
        <UpscCurrentAffairsDetailedPageHeader article={article} />
        <UpscCurrentAffairsDetailedPageContent article={article} />
      </ArticleCard>
    </PageWrap>
  );
};

export default UpscCurrentAffairsDetailedPageBody;
